import React from 'react';
import { BaseStep, useFormData, BaseStepProps } from '../BaseStep';
import FormSection from '../../../FormSection';
import industryIcon from '../../../../assets/icons/industry.png';
import productIcon from '../../../../assets/icons/product.png';
import amountIcon from '../../../../assets/icons/amount.gif';
import AmountSelector from '../AmountSelector';
import { AddButton } from '../AddButton';
import { RemoveButton } from '../RemoveButton';
import { useProfile } from '../../../../contexts/ProfileContext';
import {
  industrySelectOptions,
  canonicalIndustryList,
  modelSelectOptions,
  productTypeSelectOptions,
  stageSelectOptions,
} from '../../../../constants/industry';
import {
  MAX_INVESTMENT_AMOUNT_USD,
  MIN_INVESTMENT_AMOUNT,
} from '../../../../constants/profileFieldLimits';
import {
  minStartupAskInCurrency,
  startupAskMeetsUsdMinimum,
} from '../../../../utils/minStartupAsk';

const MAX_STARTUP_INDUSTRIES = 2;

const IndustryStep: React.FC<BaseStepProps> = ({
  userType,
  onCreateProfile,
  hasProfile,
  onComplete,
  shouldValidate,
  onValidationHandled,
  onValidationResult,
}) => {
  const { startupProfile } = useProfile();
  const storedInvestment = Number(startupProfile?.investment);
  const initialInvestment =
    Number.isFinite(storedInvestment) && storedInvestment > 0 ? String(storedInvestment) : '';

  const existingIndustries: string[] = (() => {
    const rawIndustries = startupProfile?.industries;
    const fromArray = Array.isArray(rawIndustries)
      ? rawIndustries.filter((v): v is string => typeof v === 'string' && v.trim() !== '')
      : [];
    const selected =
      fromArray.length > 0 ? fromArray : startupProfile?.industry ? [startupProfile.industry] : [];
    return (selected.length > 0 ? canonicalIndustryList(selected) : ['']).slice(
      0,
      MAX_STARTUP_INDUSTRIES
    );
  })();

  const [industryInputs, setIndustryInputs] = React.useState<string[]>(existingIndustries);

  const { formData, errors, handleInputChange, setFormData, validateForm, clearFieldError } =
    useFormData({
      industry: existingIndustries[0] || '',
      businessModel: startupProfile?.businessModel || '',
      productFocus: startupProfile?.productFocus || '',
      stage: startupProfile?.stage || '',
      currency: startupProfile?.currency || 'eur',
      investment: initialInvestment,
    });

  // State to control when to show validation errors
  const [showErrors, setShowErrors] = React.useState(false);
  // State for investment validation error
  const [investmentError, setInvestmentError] = React.useState<string | null>(null);
  const [industryError, setIndustryError] = React.useState<string | null>(null);

  // Keep primary industry + industries array in sync with row inputs
  React.useEffect(() => {
    const filtered = industryInputs.filter(s => s.trim() !== '').slice(0, MAX_STARTUP_INDUSTRIES);
    setFormData(prev => ({
      ...prev,
      industry: filtered[0] || '',
      industries: filtered,
    }));
    // Drop the required notice as soon as at least one industry is chosen.
    if (filtered.length > 0) {
      clearFieldError('industry');
      setIndustryError(null);
    }
  }, [industryInputs, setFormData, clearFieldError]);

  // Custom validation for investment amount
  const validateInvestment = React.useCallback(() => {
    const investmentValue = Number(formData.investment || '0');
    const currency = String(formData.currency || 'usd');
    if (!startupAskMeetsUsdMinimum(investmentValue, currency, null)) {
      const localMin = minStartupAskInCurrency(currency, null);
      setInvestmentError(
        `Amount asked must be at least $50,000, about ${localMin.toLocaleString()} ${currency.toUpperCase()}.`
      );
      return false;
    }
    setInvestmentError(null);
    return true;
  }, [formData.investment, formData.currency]);

  const validateIndustries = React.useCallback(() => {
    const filtered = industryInputs.filter(s => s.trim() !== '');
    if (filtered.length === 0) {
      setIndustryError('Please select at least one industry');
      return false;
    }
    if (filtered.length > MAX_STARTUP_INDUSTRIES) {
      setIndustryError('Select at most 2 industries');
      return false;
    }
    setIndustryError(null);
    return true;
  }, [industryInputs]);

  // Listen for validation requests from parent
  React.useEffect(() => {
    if (shouldValidate) {
      // Industry rows sync via industryInputs — validate those, not stale formData.industry.
      const requiredFields = ['businessModel', 'productFocus', 'stage'];
      const isValidFields = validateForm(requiredFields);
      const isValidIndustries = validateIndustries();
      const isValidInvestment = validateInvestment();

      const isValid = isValidFields && isValidIndustries && isValidInvestment;

      // Show errors after validation
      setShowErrors(true);

      // Notify parent about validation result
      if (onValidationResult) {
        onValidationResult(isValid);
      }

      // Notify parent that validation was handled
      if (onValidationHandled) {
        onValidationHandled();
      }

      // If validation passed, clear errors after a short delay
      if (isValid) {
        setTimeout(() => {
          setShowErrors(false);
        }, 100);
      }
    }
  }, [
    shouldValidate,
    validateForm,
    validateIndustries,
    validateInvestment,
    onValidationHandled,
    onValidationResult,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose form data to parent component
  React.useEffect(() => {
    if (onCreateProfile) {
      const filtered = industryInputs.filter(s => s.trim() !== '').slice(0, MAX_STARTUP_INDUSTRIES);
      onCreateProfile({
        ...formData,
        industry: filtered[0] || '',
        industries: filtered,
      });
    }
  }, [formData, industryInputs, onCreateProfile]);

  return (
    <BaseStep
      userType={userType}
      onCreateProfile={onCreateProfile}
      hasProfile={hasProfile}
      onComplete={onComplete}
    >
      <FormSection icon={industryIcon} title="Industry">
        {/* One industry, plus one more from the add button. */}
        <div>
          <label className="block text-sm sm:text-base font-medium text-white mb-2 text-left">
            Industry *
          </label>
          {industryInputs.map((value, index) => (
            <div key={index} className="mb-2">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <select
                    id={index === 0 ? 'industry' : `industry-${index}`}
                    name={index === 0 ? 'industry' : `industry-${index}`}
                    value={value}
                    required={index === 0}
                    onChange={e => {
                      const next = [...industryInputs];
                      next[index] = e.target.value;
                      setIndustryInputs(next);
                    }}
                    className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 ${
                      showErrors && industryError && !value.trim()
                        ? 'border-red-500'
                        : 'border-gray-300'
                    }`}
                  >
                    {industrySelectOptions.map(option => (
                      <option
                        key={option.value || 'placeholder'}
                        value={option.value}
                        disabled={
                          option.value !== '' &&
                          option.value !== value &&
                          industryInputs.includes(option.value)
                        }
                      >
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 pb-0.5">
                  {index === 0 && industryInputs.length < MAX_STARTUP_INDUSTRIES && (
                    <AddButton
                      onClick={() => {
                        setIndustryInputs(current =>
                          current.length < MAX_STARTUP_INDUSTRIES ? [...current, ''] : current
                        );
                      }}
                    />
                  )}
                  {index > 0 && (
                    <RemoveButton
                      onClick={() => {
                        setIndustryInputs(industryInputs.filter((_, i) => i !== index));
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
          {showErrors && industryError && (
            <p className="text-red-500 text-sm mt-1 text-right">{industryError}</p>
          )}
        </div>

        {/* Business Model Select */}
        <div>
          <label
            htmlFor="businessModel"
            className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
          >
            Business model *
          </label>
          <select
            id="businessModel"
            name="businessModel"
            value={String(formData.businessModel || '')}
            onChange={handleInputChange}
            required
            className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 ${
              showErrors && errors.businessModel ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            {modelSelectOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
          {showErrors && errors.businessModel && (
            <p className="text-red-500 text-sm mt-1 text-right">{errors.businessModel}</p>
          )}
        </div>

        {/* Stage Select */}
        <div>
          <label
            htmlFor="stage"
            className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
          >
            Stage *
          </label>
          <select
            id="stage"
            name="stage"
            value={String(formData.stage || '')}
            onChange={handleInputChange}
            required
            className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 ${
              showErrors && errors.stage ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            {stageSelectOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
          {showErrors && errors.stage && (
            <p className="text-red-500 text-sm mt-1 text-right">{errors.stage}</p>
          )}
        </div>
      </FormSection>

      <FormSection icon={productIcon} title="Product primary focus">
        <div>
          <label
            htmlFor="productFocus"
            className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
          >
            Product primary focus *
          </label>
          <select
            id="productFocus"
            name="productFocus"
            value={String(formData.productFocus || '')}
            onChange={handleInputChange}
            required
            className={`w-full px-2 py-1.5 sm:px-3 sm:py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-gray-900 ${
              showErrors && errors.productFocus ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            {(formData.productFocus === 'other'
              ? [...productTypeSelectOptions, { name: 'Other', value: 'other' }]
              : productTypeSelectOptions
            ).map(option => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
          </select>
          {showErrors && errors.productFocus && (
            <p className="text-red-500 text-sm mt-1 text-right">{errors.productFocus}</p>
          )}
        </div>
      </FormSection>

      <FormSection icon={amountIcon} title="Investment">
        <div>
          <AmountSelector
            currency={String(formData.currency || 'usd')}
            amount={Number(formData.investment || '0')}
            onCurrencyChange={cur => setFormData(prev => ({ ...prev, currency: cur }))}
            onAmountChange={val => {
              setFormData(prev => ({ ...prev, investment: String(val) }));
              // Clear error when user changes the amount
              if (investmentError) {
                setInvestmentError(null);
              }
            }}
            multiplier={10}
            min={MIN_INVESTMENT_AMOUNT}
            max={MAX_INVESTMENT_AMOUNT_USD}
            step={1000}
          />
          {showErrors && investmentError && (
            <p className="text-red-500 text-sm mt-1 text-left">{investmentError}</p>
          )}
        </div>
      </FormSection>
    </BaseStep>
  );
};

export default IndustryStep;
